import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import bp from 'styled-components-breakpoint';

import BookBike from '../../BookBike';
import Logo from '../../Logo';
import Nav from '../../Nav';
import { px2rem } from '../../../utilities/UnitHelper';
import RegionSelector from '../../RegionSelector';
import UserBadge from '../../UserBadge';
import SeriesCount from '../../SeriesCount';

const StyledEyebrow = styled.div`
  ${(props) => props.theme.typography.buttonSmall};
  background: #53565a;
  color: white;
  display: none;
  justify-content: end;

  .top-cta {
    ${(props) => props.theme.typography.bodySmall}
    color: ${(props) => props.theme.colors.yellow.primary};
    line-height: 2.75rem;
    margin-right: auto;

    svg {
      margin-bottom: -0.5rem;

      path {
        fill: ${(props) => props.theme.colors.yellow.primary};
      }
    }

    a {
      text-decoration: none;
      color: ${(props) => props.theme.colors.yellow.primary};
    }
  }

  .inner {
    display: flex;
    max-width: 75rem;
    padding: 0 1rem;
    margin: 0 auto;
  }

  #region-selector-dropdown-text,
  #userbadge-button,
  #userbadge-myaccount,
  #header-login,
  #userbadge-label {
    color: white;
  }

  #book-bike-button-no-studios {
    color: ${(props) => props.theme.colors.grayscale.black};
  }

  svg path {
    fill: white;
  }

  #userbadge-button {
    border: solid 2px white;
  }

  ${bp('tablet')`
    display: block;
  `}
`;
const StyledHeader = styled.div`
  display: none;
  min-height: 3.5rem;
  ${bp('tablet')`
    align-items: center;
    display: grid;
    grid-template-columns: 1fr 1fr auto;
    padding: 0;
    position: relative;
    z-index: ${(props) => props.theme.zIndex.nav};
  `};
  ${bp('desktopLarge')`
    grid-template-columns: 1fr 1fr repeat(3, auto);
    margin: 0 auto;
    max-width: 75rem;
    padding: 0 1rem;
  `};
  ${bp('desktopWide')`
    padding: 0 1rem;
  `};
`;

const StyledLogo = styled.div`
  align-items: center;
  box-sizing: border-box;
  display: flex;
  height: 3.5rem;
  justify-self: start;
  width: ${px2rem(202)};
  ${bp('tablet')`
    margin: 0 1rem;
    width: ${px2rem(148)};
  `};
  ${bp('desktopLarge')`
    margin: 0;
  `}
`;

const StyledNav = styled.div`
  box-sizing: border-box;
  height: 2.5rem;
  grid-column: span 2;
  grid-row: 2;
  padding: 0.25rem 1rem;
  margin-left: -0.75rem;
  white-space: nowrap;
  ${bp('tablet')`
    height: auto;
  `}
  ${bp('desktopLarge')`
    align-items: center;
    display: flex;
    height: 100%;
    grid-column: auto;
    grid-row: auto;
    justify-content: end;
    padding: 0 0 0 .5rem;
    margin-left: 0;
  `};
  ${bp('desktopWide')`
    padding: 0;
  `};
`;

const StyledRegionSelector = styled.div`
  ${(props) => props.theme.typography.bodySmall}
  align-self: center;
  display: none;
  justify-self: end;
  padding: 0 1rem;
  ${bp('tablet')`
    display: block;
    grid-row: 1;
    grid-column: 2 / span 2;
    justify-self: self-end;
    padding-left: 0;
    padding-right: 1rem;
    position: relative;
  `}
  ${bp('desktop')`
    grid-row: auto;
    grid-column: 2 / span 2;

    &.no-book-bike-btn {
      grid-column: auto;
    }
  `};
  ${bp('desktopLarge')`
    grid-row: auto;
    grid-column: auto;
  `};
`;

const StyledUserBadge = styled.div`
  align-self: center;
  grid-column: 3 / 4;
  justify-self: center;
  padding: 0 0.75rem;
  ${bp('tablet')`
    grid-column: span 2;
    grid-row: 2;

    &.no-book-bike-btn {
      grid-row: 1;
      padding: 0;
    }
  `};
  ${bp('desktop')`
    grid-column: span 2;
    grid-row: 2;

    &.no-book-bike-btn {
      grid-row: auto;
    }
  `};
  ${bp('desktopLarge')`
    grid-column: auto;
    grid-row: auto;
    justify-self: end;
    padding: 0;
  `};
`;

const StyledButton = styled.div`
  align-items: center;
  display: flex;
  grid-column: 2 / span 2;
  grid-row: 1;
  justify-self: end;
  ${bp('tablet')`
    grid-column: 4;
    justify-self: right;
  `};
  ${bp('desktop')`
    grid-column: 4;
    grid-row: auto;
    position: relative;
  `};
  ${bp('desktopLarge')`
    grid-column: 5;
  `};
`;

/**
 * SCS responsive default header component.
 *
 * TODO: Add missing components (subnav, series credits, etc).
 */
function HeaderDefault({
  id,
  className,
  currentPath,
  currentRegion,
  LinkWrapper,
  navItems,
  onRegionChange,
  onRegionToggle,
  onLogoClick,
  onNavItemClick,
  onStudioSelect,
  onSubNavItemClick,
  regions,
  userBadgeProps,
  seriesCount,
  showSeriesCount,
  studios,
  showBookBikeButton,
  ...props
}) {
  const [activeSubNav, _setActiveSubNav] = useState([]);
  const activeSubNavRef = useRef(activeSubNav);

  const setActiveSubNav = (subNav) => {
    activeSubNavRef.current = subNav;
    _setActiveSubNav(subNav);
  };

  useEffect(() => {
    // finds a nav item with a subNav that matches the current path
    const navItemWithActiveSubNav = navItems.find(
      (item) =>
        item.subNav &&
        (item.subNav.some((subNavItem) => subNavItem.url === currentPath) ||
          item.subNav.some(
            (subNavItem) =>
              subNavItem.matchingPatterns &&
              subNavItem.matchingPatterns.some((regex) =>
                regex.test(currentPath)
              )
          ))
    );

    const activeSubNavItem = navItemWithActiveSubNav
      ? navItemWithActiveSubNav.subNav
      : [];

    setActiveSubNav(activeSubNavItem);
  }, [navItems, currentPath]);

  const showSeries = userBadgeProps && userBadgeProps.user && showSeriesCount;
  return (
    <>
      <StyledEyebrow className={className}>
        <div className="inner">
          <div className="top-cta">
            <a href="/new-to-soul/">New to Soul?</a>
          </div>
          <StyledRegionSelector
            className={`${!showBookBikeButton ? 'no-book-bike-btn' : ''}`}
          >
            <RegionSelector
              currentRegion={currentRegion}
              regions={regions}
              onRegionChange={onRegionChange}
              onToggleClick={onRegionToggle}
            />
          </StyledRegionSelector>
          <StyledUserBadge
            className={`headerDefault__UserBadge ${
              !showBookBikeButton ? 'no-book-bike-btn' : ' '
            }`}
          >
            <UserBadge {...userBadgeProps} />
          </StyledUserBadge>
          <StyledButton>
            {showBookBikeButton ? (
              <BookBike
                region={currentRegion}
                studios={studios}
                onChangeHandler={onStudioSelect}
              />
            ) : null}
          </StyledButton>
          {showSeries ? <SeriesCount count={seriesCount} /> : null}
        </div>
      </StyledEyebrow>
      <StyledHeader id={id} {...props}>
        <StyledLogo>
          <Logo
            id="soulcycle-logo"
            ariaLabel="SoulCycle Home Page"
            LinkWrapper={LinkWrapper}
            onClick={onLogoClick}
          />
        </StyledLogo>
        <StyledNav>
          <Nav
            initialUrl={currentPath}
            items={navItems}
            onClick={onNavItemClick}
            LinkWrapper={LinkWrapper}
          />
        </StyledNav>
      </StyledHeader>
    </>
  );
}

HeaderDefault.displayName = 'HeaderDefault';

/**
 * propTypes for this component
 */
HeaderDefault.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  /** The current active pathname of the url */
  currentPath: PropTypes.string.isRequired,
  /**
   * The current region
   */
  currentRegion: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
  }).isRequired,
  /**
   *  Nav items array
   */
  navItems: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      url: PropTypes.string,
    })
  ).isRequired,
  /**
   *  Regions array
   */
  regions: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        title: PropTypes.string.isRequired,
      })
    )
  ).isRequired,
  /**
   * Function to execute when region is changed
   */
  onRegionChange: PropTypes.func,
  /**
   * Function to execute when region menu button is toggled
   */
  onRegionToggle: PropTypes.func,
  /**
   * Click handler for the logo
   */
  onLogoClick: PropTypes.func,
  /**
   * Click handler for the nav items
   */
  onNavItemClick: PropTypes.func,
  /**
   * onChange handler for Book A Bike studios items
   */
  onStudioSelect: PropTypes.func,
  /**
   * Click handler for the subnav items
   */
  onSubNavItemClick: PropTypes.func,
  /**
   * Collection of props for the user badge component.
   */
  userBadgeProps: PropTypes.shape({
    /**
     * List of URLs to fill the UserBadge drop-down.
     */
    urlList: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
      })
    ),
    /**
     *  User object
     */
    user: PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      id: PropTypes.string,
    }),
    /**
     *  Fires when user selects a different item in the user badge.
     */
    onChange: PropTypes.func,
    /**
     *  Fires when user click on an item within the user badge.
     */
    onClick: PropTypes.func,
  }),
  /**
   * Number of series in Series Count
   */
  seriesCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  /**
   * Boolean indicating if Series Count should be displayed
   */
  showSeriesCount: PropTypes.bool.isRequired,
  /**
   * List of all studios.
   */
  studios: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      title: PropTypes.string.isRequired,
    })
  ).isRequired,
  /**
   * Wrapper for Next.js Link or React Router Link
   */
  LinkWrapper: PropTypes.func,
  /**
   * Show the "Book a Bike" button or not
   */
  showBookBikeButton: PropTypes.bool,
};

/**
 * Default propTypes
 */
HeaderDefault.defaultProps = {
  id: 'header',
  className: '',
  userBadgeProps: {
    user: null,
    urlList: [],
  },
  onRegionChange: () => {},
  onRegionToggle: () => {},
  onLogoClick: null,
  onNavItemClick: null,
  onStudioSelect: null,
  onSubNavItemClick: null,
  LinkWrapper: null,
  showBookBikeButton: true,
};

/* @component */
export default HeaderDefault;
